import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Trash2, Wallet } from 'lucide-react';
import { getErrorMessage, normalizeError } from '@/lib/api/errors';
import type { Payout } from '@/lib/api/payouts';
import { formatTime } from '@/lib/datetime';
import { formatMoney } from '@/lib/money';
import { usePermissions } from '@/hooks/usePermissions';
import {
  Alert,
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Modal,
  MoneyInput,
  Spinner,
  toast,
} from '@/components/ui';
import { closeCashPayout, useCashPayoutOpen } from './payoutStore';
import { useCreatePayout, useDeletePayout, usePayoutsToday } from './hooks';

const MAX_PAYOUT = 10_000_000;
const QUICK_REASONS = [
  'Groceries / supplies',
  'Gas',
  'Transport',
  'Staff advance',
  'Repairs',
  'Cleaning items',
];

const payoutSchema = z.object({
  amount: z
    .number('Enter an amount greater than zero')
    .gt(0, 'Enter an amount greater than zero')
    .max(MAX_PAYOUT, 'Amount is too large'),
  // The API collapses whitespace; do the same so what we validate is what is stored.
  reason: z
    .string()
    .transform((s) => s.replace(/\s+/g, ' ').trim())
    .pipe(z.string().min(1, 'Enter a reason').max(255, 'At most 255 characters')),
});
type FormInput = z.input<typeof payoutSchema>;
type FormValues = z.output<typeof payoutSchema>;

/** Global "Cash paid out" dialog: mounted once by <ShiftBanner/>, opened by openCashPayout() / Alt+E. */
export function CashPayoutModal() {
  const open = useCashPayoutOpen();
  if (!open) return null;
  return <PayoutDialog onClose={closeCashPayout} />;
}

function PayoutDialog({ onClose }: { onClose: () => void }) {
  const { isAdmin } = usePermissions();
  const today = usePayoutsToday(true);
  const create = useCreatePayout();
  const del = useDeletePayout();
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Payout | null>(null);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(payoutSchema),
    defaultValues: { amount: undefined as unknown as number, reason: '' },
  });

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    try {
      const saved = await create.mutateAsync({ amount: v.amount, reason: v.reason });
      toast.success(`Cash paid out: ${formatMoney(saved.amount)} · ${saved.reason}`);
      onClose();
    } catch (err) {
      const e = normalizeError(err);
      if (e.isValidation && (e.errors.amount || e.errors.reason)) {
        if (e.errors.amount) setError('amount', { type: 'server', message: e.errors.amount[0] });
        if (e.errors.reason) setError('reason', { type: 'server', message: e.errors.reason[0] });
      } else {
        setFormError(e.message);
      }
    }
  });

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success('Payout deleted');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setDeleting(null);
  };

  const rows = today.data?.data ?? [];
  const formId = 'cash-payout-form';

  return (
    <>
      <Modal
        open
        onClose={onClose}
        // Esc / backdrop belong to the delete confirmation while it is open.
        dismissible={!isSubmitting && deleting === null}
        title={
          <span className="flex items-center gap-2">
            <Wallet aria-hidden className="size-5 text-danger" />
            Cash paid out
          </span>
        }
        description="Money taken from the cash drawer for an expense."
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form={formId} loading={isSubmitting}>
              Save payout
            </Button>
          </>
        }
      >
        <form
          id={formId}
          onSubmit={onSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void onSubmit();
            }
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          {formError && <Alert tone="error">{formError}</Alert>}
          <FormField label="Amount taken" error={errors.amount?.message} required>
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <MoneyInput
                  size="lg"
                  value={(field.value as number | undefined) ?? null}
                  onValueChange={(v) => field.onChange(v ?? undefined)}
                  onBlur={field.onBlur}
                  max={MAX_PAYOUT}
                  data-autofocus
                />
              )}
            />
          </FormField>
          <FormField label="Reason / paid for" error={errors.reason?.message} required>
            <Input
              size="lg"
              maxLength={255}
              placeholder="e.g. Vegetables from market"
              {...register('reason')}
            />
          </FormField>
          <div className="-mt-2 flex flex-wrap gap-1.5" aria-label="Quick reasons" role="group">
            {QUICK_REASONS.map((label) => (
              <button
                key={label}
                type="button"
                className="rounded-full border border-border px-2.5 py-1 text-xs font-medium text-fg-muted hover:bg-surface-3 hover:text-fg"
                onClick={() =>
                  setValue('reason', label, { shouldValidate: true, shouldDirty: true })
                }
              >
                {label}
              </button>
            ))}
          </div>
        </form>

        <section aria-label="Paid out today" className="mt-5 border-t border-border pt-3">
          <div className="mb-1.5 flex items-baseline justify-between text-sm">
            <h3 className="font-semibold">Paid out today</h3>
            {today.data && (
              <span className="tabular font-semibold">{formatMoney(today.data.meta.total)}</span>
            )}
          </div>
          {today.isPending ? (
            <div className="flex justify-center py-3">
              <Spinner label="Loading today's payouts" />
            </div>
          ) : today.isError ? (
            <Alert tone="error">
              {getErrorMessage(today.error)}{' '}
              <button className="font-medium underline" onClick={() => void today.refetch()}>
                Try again
              </button>
            </Alert>
          ) : rows.length === 0 ? (
            <p className="text-sm text-fg-muted">Nothing paid out yet today.</p>
          ) : (
            <ul className="max-h-40 divide-y divide-border overflow-y-auto text-sm">
              {rows.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="min-w-0 truncate">
                    <span className="mr-2 text-fg-subtle">{formatTime(p.paid_at)}</span>
                    {p.reason}
                    {isAdmin && p.user_name && (
                      <span className="ml-2 text-xs text-fg-subtle">({p.user_name})</span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="tabular">{formatMoney(p.amount)}</span>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Delete payout ${p.reason}`}
                        className="text-danger hover:text-danger"
                        onClick={() => setDeleting(p)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <p className="mt-3 text-center text-xs text-fg-subtle">
          Shortcut: Alt+E · Ctrl+Enter to save · Esc to close
        </p>
      </Modal>
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this payout?"
        message={
          <>
            <strong>{deleting ? formatMoney(deleting.amount) : ''}</strong> for “{deleting?.reason}”
            will be removed and the shift&apos;s expected cash will go up by that amount. This is
            recorded in the activity log.
          </>
        }
        confirmLabel="Delete payout"
        loading={del.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
